import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { Header } from './components/WeatherMap/Header';
import { hydrateRemoteConfig } from './config/remoteConfig';
import { applyRemoteModels, applyRemoteParticles } from './store/applyRemoteModels';
import { LanguageProvider, useTranslation } from './i18n/LanguageContext';
import { trackVisit } from './utils/trackVisit';
import { fetchRasterMeta, rasterDirFor } from './services/modelRunInfo';
import { useWeatherStore } from './store/weatherStore';
import './index.css';
import './components/WeatherMap/WeatherMap.css';

const loadWeatherMap = () =>
    import('./components/WeatherMap/NewWeatherMap').then((module) => ({
        default: module.NewWeatherMap,
    }));
const NewWeatherMap = lazy(loadWeatherMap);

// الداشبورد chunk منفصل تماماً — لا يؤثر على حزمة الخريطة.
const AdminApp = lazy(() => import('./admin/AdminApp'));

function PageTracker() {
    const location = useLocation();
    useEffect(() => {
        void trackVisit(location.pathname);
    }, [location.pathname]);
    return null;
}

function WeatherMapFallback() {
    return (
        <div className="weather-map-container dark">
            <Header />
            <div
                className="fixed inset-0 w-screen h-[100dvh] overflow-hidden [contain:size_layout_paint] bg-[#0a0e14] bg-[radial-gradient(circle_at_20%_30%,rgba(49,119,164,0.36),transparent_34%),radial-gradient(circle_at_72%_62%,rgba(90,170,124,0.28),transparent_38%)]"
                aria-hidden="true"
            />
        </div>
    );
}

function AdminLoadingFallback() {
    const { t } = useTranslation();
    return <div style={{ padding: 40, textAlign: 'center' }}>{t('common.loading')}</div>;
}

/**
 * تُرطَّب إعدادات الأدمن (ألوان/طبقات/نماذج) قبل تركيب الخريطة كي تُبنى خامات
 * WebGL بالألوان الصحيحة من أول مرة. أي فشل/مهلة ⇒ افتراضيات الكود (لا كسر).
 */
function MapWithRemoteConfig() {
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let cancelled = false;
        // نبدأ تنزيل حزمة الخريطة بالتوازي مع جلب الإعدادات (لا بعده) — نفس الـ promise
        // الذي يستخدمه lazy() فيُعاد استخدامه من الكاش، فلا يوجد طلب مضاعف.
        void loadWeatherMap();
        // وmeta.json كذلك: الخريطة لا تطلبه إلا بعد تركيبها (~1.2ث بعد انتهاء الإعدادات)،
        // وكل إطار ينتظره. fetchRasterMeta يشارك الطلب نفسه بالرابط، فحين تطلبه الخريطة
        // تجده جاهزاً أو في الطريق — لا طلب مكرّر. النموذج المحفوظ قد يتبدّل بعد الإعدادات
        // (نموذج أُوقف)، وحينها يُطلب الصحيح كالمعتاد؛ الخسارة ملفّ صغير واحد.
        void fetchRasterMeta(rasterDirFor(useWeatherStore.getState().selectedModel));
        hydrateRemoteConfig()
            // ‏applyRemoteModels خارج شرط النجاح عمداً: هو أيضاً من يقيس النموذج
            // المختار المحفوظ إلى القائمة المتاحة، ونموذجٌ أُوقف (كـICON) يبقى محفوظاً
            // في localStorage عند الزائر. فلو عُلّق على نجاح الجلب لصار فشلُ الإعدادات
            // — وهو أكثر ما يُتوقّع عند انقطاع الشبكة — يترك ذلك الزائر أمام خريطة
            // فارغة تطلب مجلّد نموذجٍ لم يعد موجوداً. وكل فروعه محروسة بلا قيم بعيدة.
            .then((ok) => { applyRemoteModels(); if (ok) applyRemoteParticles(); })
            .finally(() => { if (!cancelled) setReady(true); });
        return () => { cancelled = true; };
    }, []);

    if (!ready) return <WeatherMapFallback />;

    return (
        <Suspense fallback={<WeatherMapFallback />}>
            <NewWeatherMap />
        </Suspense>
    );
}

function App() {
    return (
        <LanguageProvider>
            <BrowserRouter>
                <PageTracker />
                <Routes>
                    <Route path="/" element={<MapWithRemoteConfig />} />
                    <Route
                        path="/admin/*"
                        element={
                            <Suspense fallback={<AdminLoadingFallback />}>
                                <AdminApp />
                            </Suspense>
                        }
                    />
                    <Route path="*" element={<MapWithRemoteConfig />} />
                </Routes>
            </BrowserRouter>
        </LanguageProvider>
    );
}

export default App;
